import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Video, Plus, Copy, ExternalLink, Send, Trash2, Loader2, Calendar, Zap } from "lucide-react";

const joinUrl = (token) => `${window.location.origin}/call/${token}`;
const hostUrl = (token) => `${window.location.origin}/call/${token}?host=1`;

const fmtWhen = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("en-NZ", { weekday: "short", day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function VideoSupport() {
  const [meetings, setMeetings] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "Fieldlink demo", customer_name: "", customer_email: "", company: "", scheduled: false, scheduled_at: "", send_email: true });

  const load = useCallback(() => {
    api.get("/video/meetings").then((r) => setMeetings(r.data)).catch(() => setMeetings([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const body = {
        title: form.title || "Fieldlink demo",
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        company: form.company,
        scheduled_at: form.scheduled && form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        send_email: form.send_email,
      };
      const r = await api.post("/video/meetings", body);
      const emailed = form.send_email && form.customer_email;
      toast.success(emailed ? `Invite sent to ${form.customer_email}` : "Video call created");
      await navigator.clipboard?.writeText(joinUrl(r.data.token)).catch(() => {});
      setForm({ title: "Fieldlink demo", customer_name: "", customer_email: "", company: "", scheduled: false, scheduled_at: "", send_email: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create the video call");
    } finally { setCreating(false); }
  };

  const copy = (token) => { navigator.clipboard?.writeText(joinUrl(token)); toast.success("Join link copied"); };
  const resend = async (m) => {
    try { await api.post(`/video/meetings/${m.id}/resend`); toast.success(`Invite re-sent to ${m.customer_email}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not send invite"); }
  };
  const cancel = async (m) => {
    if (!window.confirm("Cancel this video call? The link will stop working.")) return;
    try { await api.delete(`/video/meetings/${m.id}`); toast.success("Call cancelled"); load(); }
    catch { toast.error("Could not cancel"); }
  };

  return (
    <section className="space-y-6" data-testid="video-support-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#FF5F15]/15 flex items-center justify-center"><Video className="w-6 h-6 text-[#FF5F15]" /></div>
        <div>
          <h1 className="text-2xl font-black tracking-tight">Video Support</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">Start a live 1:1 video demo with screen sharing and email the prospect a join link.</p>
        </div>
      </div>

      <form onSubmit={create} className="hard-card p-5 grid md:grid-cols-2 gap-4" data-testid="video-create-form">
        <Field label="Meeting title">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} data-testid="video-title" placeholder="Fieldlink demo" />
        </Field>
        <Field label="Company">
          <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inp} data-testid="video-company" placeholder="e.g. ABC Civil Ltd" />
        </Field>
        <Field label="Customer name">
          <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inp} data-testid="video-customer-name" placeholder="Jane Smith" />
        </Field>
        <Field label="Customer email (invite is sent here)">
          <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className={inp} data-testid="video-customer-email" placeholder="jane@abccivil.co.nz" />
        </Field>

        <div className="md:col-span-2 flex flex-wrap items-center gap-4">
          <div className="inline-flex rounded-lg border border-zinc-300 dark:border-white/15 overflow-hidden">
            <button type="button" onClick={() => setForm({ ...form, scheduled: false })} data-testid="video-mode-instant"
              className={`px-3.5 h-9 text-sm font-semibold flex items-center gap-1.5 ${!form.scheduled ? "bg-[#FF5F15] text-white" : "text-zinc-600 dark:text-zinc-300"}`}><Zap className="w-4 h-4" /> Instant</button>
            <button type="button" onClick={() => setForm({ ...form, scheduled: true })} data-testid="video-mode-scheduled"
              className={`px-3.5 h-9 text-sm font-semibold flex items-center gap-1.5 ${form.scheduled ? "bg-[#FF5F15] text-white" : "text-zinc-600 dark:text-zinc-300"}`}><Calendar className="w-4 h-4" /> Scheduled</button>
          </div>
          {form.scheduled && (
            <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className={`${inp} w-auto`} data-testid="video-scheduled-at" />
          )}
          <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid="video-send-email" className="accent-[#FF5F15] w-4 h-4" />
            Email the join link to the customer now
          </label>
        </div>

        <div className="md:col-span-2">
          <button type="submit" disabled={creating} data-testid="video-create-submit"
            className="h-12 px-6 rounded-md bg-[#FF5F15] hover:bg-[#ff6f2e] disabled:opacity-70 text-white font-black uppercase tracking-[0.1em] text-sm flex items-center gap-2 transition-colors">
            {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Create video call
          </button>
        </div>
      </form>

      <div className="space-y-3" data-testid="video-meetings-list">
        {meetings === null && <div className="text-sm text-zinc-500">Loading…</div>}
        {meetings?.length === 0 && <div className="hard-card p-8 text-center text-zinc-500">No video calls yet — create one above to get started.</div>}
        {meetings?.map((m) => {
          const cancelled = m.status === "cancelled";
          return (
            <div key={m.id} className={`hard-card p-4 flex flex-wrap items-center gap-3 ${cancelled ? "opacity-55" : ""}`} data-testid={`video-meeting-${m.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold truncate">{m.title}</span>
                  <StatusBadge status={m.status} />
                  {m.email_sent && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-500">Invite sent</span>}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
                  {[m.customer_name, m.company, m.customer_email].filter(Boolean).join(" · ") || "No customer details"}
                  {m.scheduled_at ? ` · ${fmtWhen(m.scheduled_at)}` : " · Join anytime"}
                </div>
              </div>
              {!cancelled && (
                <div className="flex items-center gap-2">
                  <a href={hostUrl(m.token)} target="_blank" rel="noreferrer" data-testid={`video-join-${m.id}`}
                    className="h-9 px-3.5 rounded-md bg-[#FF5F15] hover:bg-[#ff6f2e] text-white text-sm font-bold flex items-center gap-1.5"><ExternalLink className="w-4 h-4" /> Join call</a>
                  <button onClick={() => copy(m.token)} data-testid={`video-copy-${m.id}`} className="h-9 px-3 rounded-md border border-zinc-300 dark:border-white/15 text-sm font-semibold flex items-center gap-1.5 hover:bg-zinc-100 dark:hover:bg-white/5"><Copy className="w-4 h-4" /> Copy link</button>
                  {m.customer_email && <button onClick={() => resend(m)} data-testid={`video-resend-${m.id}`} className="h-9 px-3 rounded-md border border-zinc-300 dark:border-white/15 text-sm font-semibold flex items-center gap-1.5 hover:bg-zinc-100 dark:hover:bg-white/5"><Send className="w-4 h-4" /> Resend</button>}
                  <button onClick={() => cancel(m)} data-testid={`video-cancel-${m.id}`} className="h-9 w-9 rounded-md border border-red-300/40 text-red-500 flex items-center justify-center hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

const inp = "w-full h-11 rounded-md border border-zinc-300 dark:border-white/15 bg-white dark:bg-[rgba(7,18,28,0.6)] px-3 text-sm outline-none focus:border-[#FF5F15] text-zinc-900 dark:text-white";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500 dark:text-white/55 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function StatusBadge({ status }) {
  const map = {
    instant: ["Instant", "bg-sky-500/15 text-sky-500"],
    scheduled: ["Scheduled", "bg-amber-500/15 text-amber-500"],
    cancelled: ["Cancelled", "bg-zinc-500/15 text-zinc-500"],
  };
  const [label, cls] = map[status] || ["", ""];
  if (!label) return null;
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${cls}`}>{label}</span>;
}
